import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import DocumentSigningDetailsDrawer from './DocumentSigningDetailsDrawer';

const STATE_CHIP_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  Draft: 'default',
  FieldsConfigured: 'info',
  PendingSignatures: 'warning',
  SigningInProgress: 'warning',
  SigningCompleted: 'info',
  PdfEmbedding: 'info',
  Completed: 'success',
  Rejected: 'error',
  Cancelled: 'error',
};

const SIGNER_STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  viewed: 'info',
  signed: 'success',
  rejected: 'error',
};

const SignerPortalPage: React.FC = () => {
  const { documentSigningApiWrapper }: Api = useApi();
  const toast = useToastMessageQueue();

  const [documents, setDocuments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [errors] = useState({});

  const [paginationModel, setPaginationModel] = usePaginationPreference('signerPortal', 25);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentSigningApiWrapper.searchMyDocuments('1.0', term || undefined, paginationModel.page + 1, paginationModel.pageSize);
      setDocuments(res.data?.data?.queryResult ?? []);
      setTotal(res.data?.data?.totalRecords ?? 0);
    } catch (err) {
      handleAPIError(err, toast, errors);
    } finally {
      setLoading(false);
    }
  }, [term, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'title', headerName: intl.get('documentSigning.title') || 'Title', flex: 1 },
    {
      field: 'workflowState',
      headerName: intl.get('documentSigning.status') || 'Status',
      width: 160,
      renderCell: (params) => {
        const stateName: string = params.row.workflowState?.name ?? '';
        return (
          <Chip
            label={intl.get(`documentSigning.workflowState.${stateName}`) || stateName}
            color={STATE_CHIP_COLOR[stateName] ?? 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'myStatus',
      headerName: intl.get('signerPortal.myStatus') || 'My Status',
      width: 130,
      renderCell: (params) => {
        const mySigner = (params.row.signers ?? []).find((s: any) => s.status);
        const status: string = mySigner?.status ?? 'pending';
        return (
          <Chip
            label={intl.get(`documentSigning.signer.status.${status}`) || status}
            color={SIGNER_STATUS_COLOR[status] ?? 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_date',
      headerName: intl.get('common.createdDate') || 'Created',
      width: 140,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : '',
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Button size="small" onClick={() => setSelectedId(params.row.id)}>
          {intl.get('common.view') || 'View'}
        </Button>
      ),
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">{intl.get('signerPortal.pageTitle') || 'My Signing Documents'}</Typography>
        </Box>

        <TextField
          size="small"
          placeholder={intl.get('common.search') || 'Search...'}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ mb: 2, width: 320 }}
        />

        {loading ? (
          <Spinner />
        ) : (
          <DataGrid
            rows={documents}
            columns={columns}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            disableRowSelectionOnClick
          />
        )}

        {selectedId !== null && (
          <DocumentSigningDetailsDrawer
            documentId={selectedId}
            open={selectedId !== null}
            onClose={() => setSelectedId(null)}
            onRefresh={fetchDocuments}
          />
        )}
      </Box>
    </Card>
  );
};

export default SignerPortalPage;
